<template>
    <div>
        <p class="is-italic has-text-info clarity-mask">
            <template v-if="isKata">
                {{
                    translate("audit_logs.template.fightFinishedKata", false, [
                        context.categoryName,
                        registrationLabel(context.firstEventRegistrationId, context.firstCompetitorName),
                    ])
                }}
            </template>
            <template v-else>
                {{
                    translate("audit_logs.template.fightFinished", false, [
                        context.categoryName,
                        registrationLabel(context.firstEventRegistrationId, context.firstCompetitorName),
                        registrationLabel(context.secondEventRegistrationId, context.secondCompetitorName),
                        context.winnerCompetitorName,
                    ])
                }}
            </template>
        </p>
        <p>
            {{ translate("common.category") }}: <span class="has-text-weight-bold">{{ context.categoryName }}</span
            >, {{ translate("common.round") }}:
            <span class="has-text-weight-bold">
                <FightRoundName :round-name="context.fightRound" :bracket-level="context.bracketLevel" />
            </span>
            <template v-if="context.fightNumber">
                ,
                {{ translate("common.fight_number") }}:
                <span class="tag is-white">#{{ context.fightNumber }}</span>
            </template>
            <template v-if="context.matName && context.matName !== '---'">
                , {{ translate("common.mat") }}: <span class="has-text-weight-bold">{{ context.matName }}</span>
            </template>
        </p>
        <p v-if="isKata" class="clarity-mask">
            {{ translate("common.competitor") }}:
            <span class="has-text-weight-bold" :class="{ 'has-text-success': isWinner(context.firstEventRegistrationId) }">
                {{ registrationLabel(context.firstEventRegistrationId, context.firstCompetitorName) }}
            </span>
            <template v-if="kataTotalScore !== null">
                &mdash; {{ translate("fight.result.details.points") }} {{ kataTotalScore }}
            </template>
        </p>
        <p v-else class="clarity-mask">
            {{ translate("common.competitors") }}:
            <span class="has-text-weight-bold" :class="{ 'has-text-success': isWinner(context.firstEventRegistrationId) }">
                {{ registrationLabel(context.firstEventRegistrationId, context.firstCompetitorName) }}
            </span>
            vs.
            <span class="has-text-weight-bold" :class="{ 'has-text-success': isWinner(context.secondEventRegistrationId) }">
                {{ registrationLabel(context.secondEventRegistrationId, context.secondCompetitorName) }}
            </span>
        </p>
        <div v-if="!isKata" class="fight-finished-result">
            <FightResultDetailsTooltip :match="matchForDetails" force-visible />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import { FightFinished } from "../../Api/auditLogsApi";
    import { translate } from "../../Common/translations";
    import { User } from "../../Common/types";
    import { BracketMatch, FightResult, KataResult } from "../../Common/Fights/types";
    import { BracketTypeEnum } from "../../Common/types";
    import FightRoundName from "../../Common/FightRoundName.vue";
    import FightResultDetailsTooltip from "../../Results/FightResultDetailsTooltip.vue";
    import { registrationLabel } from "./registrationLabel";

    const props = defineProps<{
        context: FightFinished;
        user: User;
    }>();

    const isKata = computed(() => props.context.bracketType === BracketTypeEnum.Kata);

    const kataTotalScore = computed<number | null>(() => {
        return isKata.value ? (props.context.result as KataResult).totalScore : null;
    });

    function isWinner(eventRegistrationId: number | null): boolean {
        if (isKata.value) {
            return eventRegistrationId !== null && eventRegistrationId === (props.context.result as KataResult).competitorId;
        }
        return eventRegistrationId === (props.context.result as FightResult).winnerCompetitorId;
    }

    const matchForDetails = computed<BracketMatch>(() => ({
        player1: {
            id: props.context.firstEventRegistrationId,
            data: { publicId: "", firstName: "", lastName: "", academy: "", academyId: 0, photoUrl: null },
            winner: isWinner(props.context.firstEventRegistrationId),
            points: null,
        },
        player2: {
            id: props.context.secondEventRegistrationId,
            data: { publicId: "", firstName: "", lastName: "", academy: "", academyId: 0, photoUrl: null },
            winner: isWinner(props.context.secondEventRegistrationId),
            points: null,
        },
        result: props.context.result as FightResult,
    }));
</script>

<style scoped>
    .fight-finished-result :deep(.fight-tooltip) {
        display: block;
        position: static;
        width: auto;
        max-width: 320px;
        margin-top: 0.5rem;
        filter: none;
    }

    .fight-finished-result :deep(.fight-tooltip::after) {
        display: none;
    }
</style>
